import * as React from 'react';
import * as SheetPrimitive from '@radix-ui/react-dialog';
import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '../../lib/utils';
import {
  SHEET_OVERLAY,
  SHEET_CONTENT_BASE,
  SHEET_SLIDES,
} from '../../shared/styles/animations';
import {
  SAFE_AREA_BOTTOM,
  SAFE_AREA_TOP,
} from '../../shared/styles/layout.constants';
import {
  ModalSurfaceContent,
  ModalSurfaceRoot,
  useModalSurfaceContentRef,
} from './modal-scroll-lock';

/** Publishes its modality for `PopoverContent` — see `modal-scroll-lock`. */
const Sheet = ({
  modal = true,
  ...props
}: React.ComponentPropsWithoutRef<typeof SheetPrimitive.Root>) => (
  <ModalSurfaceRoot modal={modal}>
    <SheetPrimitive.Root modal={modal} {...props} />
  </ModalSurfaceRoot>
);
Sheet.displayName = 'Sheet';

const SheetTrigger = SheetPrimitive.Trigger;

const SheetClose = SheetPrimitive.Close;

const SheetPortal = SheetPrimitive.Portal;

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    className={cn('fixed inset-0 z-50 bg-black/80', SHEET_OVERLAY, className)}
    {...props}
    ref={ref}
  />
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

const sheetVariants = cva(
  cn('fixed z-50 gap-4 bg-background p-6 shadow-lg', SHEET_CONTENT_BASE),
  {
    variants: {
      // A sheet is `position: fixed`, so it is laid out against the VIEWPORT —
      // which on an edge-to-edge Android shell runs behind the status bar and
      // the navigation bar. `html`'s own safe-area padding cannot reach it, so
      // the panel carries the inset itself.
      //
      // A top/bottom sheet spans the bar it is anchored to, so its SURFACE
      // should still run to the physical screen edge and only its CONTENT is
      // pushed clear — hence padding rather than an offset. A left/right sheet
      // is not anchored to either bar, so it is inset as a box instead (see
      // `safeAreaStyle` below).
      //
      // A caller that zeroes the padding (`p-0` — DialogForm, ResponsiveDialog)
      // takes the obligation on: tailwind-merge drops this class, and that
      // caller must inset its own bottom-most region. Both worked examples do.
      side: {
        top: cn(
          'inset-x-0 top-0 border-b pt-[calc(1.5rem+var(--safe-top,0px))]',
          SHEET_SLIDES.top
        ),
        bottom: cn(
          'inset-x-0 bottom-0 border-t pb-[calc(1.5rem+var(--safe-bottom,0px))]',
          SHEET_SLIDES.bottom
        ),
        left: cn(
          'left-0 w-3/4 border-r sm:max-w-sm',
          SHEET_SLIDES.left
        ),
        right: cn(
          'right-0 w-3/4 border-l sm:max-w-sm',
          SHEET_SLIDES.right
        ),
      },
    },
    defaultVariants: {
      side: 'right',
    },
  }
);

interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
    VariantProps<typeof sheetVariants> {
  /** Hide the default close button (useful when content provides its own) */
  hideCloseButton?: boolean;
  /** Hide the overlay backdrop (useful for non-modal side panels) */
  hideOverlay?: boolean;
}

const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetContentProps
>(({ side = 'right', className, children, hideCloseButton = false, hideOverlay = false, style, ...props }, ref) => {
  const { t } = useTranslation('common');
  const { contentRef, setContentRef } = useModalSurfaceContentRef(ref);
  // Left/right sheets stretch the full height, so they are inset as a box — the
  // panel stops at the status bar and at the navigation bar. Top/bottom sheets
  // instead PAD their content (see sheetVariants above), so the surface still
  // runs to the screen edge. Both insets are 0px off an edge-to-edge shell, so
  // this is a no-op on the web and on desktop.
  const safeAreaStyle = side === 'left' || side === 'right'
    ? { top: SAFE_AREA_TOP, bottom: SAFE_AREA_BOTTOM, ...style }
    : style;

  return (
  <SheetPortal>
    {!hideOverlay && <SheetOverlay />}
    <SheetPrimitive.Content
      ref={setContentRef}
      className={cn(sheetVariants({ side }), className)}
      style={safeAreaStyle}
      {...props}
    >
      <ModalSurfaceContent contentRef={contentRef} hasOverlay={!hideOverlay}>
        {children}
      </ModalSurfaceContent>
      {!hideCloseButton && (
        <SheetPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary">
          <X className="h-4 w-4" />
          <span className="sr-only">{t('app.close')}</span>
        </SheetPrimitive.Close>
      )}
    </SheetPrimitive.Content>
  </SheetPortal>
  );
});
SheetContent.displayName = SheetPrimitive.Content.displayName;

const SheetHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col space-y-2 text-center sm:text-left',
      className
    )}
    {...props}
  />
);
SheetHeader.displayName = 'SheetHeader';

const SheetFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2',
      className
    )}
    {...props}
  />
);
SheetFooter.displayName = 'SheetFooter';

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold text-foreground', className)}
    {...props}
  />
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

export {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
